import React from "react"
import { isMobileOnly } from "react-device-detect"
import clsx from "clsx"
import { useTranslation } from "react-i18next"
import * as Ibantools from "ibantools"

import FormElement from "components/Form/FormElement"
import * as TeamApi from "features/api/TeamApi"
import { useAuth, useBikeStoreProcess } from "hooks"
import { isIBANValid, isNameValidYouSign, isNotEmpty } from "utils"
import { IBAN_MAX_LENGTH } from "utils/constants"
import { FormError } from "models/form"

export type InfosFormFields = {
	iban?: string
	owner?: string
	phone?: string
	date?: Date
	sellerId?: string
	sellerData?: {
		user_id: string
		mail: string
		description: {
			prenom: string
			nom: string
		}
	}
	last_name?: string
	first_name?: string
}

const defaultFormFields: InfosFormFields = {
	iban: "",
	owner: "",
	phone: "",
	date: new Date(),
	sellerId: "",
	sellerData: null,
	last_name: "",
	first_name: "",
}

export const InsuranceInfos = ({
	onSubmit,
	priceYear,
	priceMonth,
	existingContract,
	disabled,
	onCancel,
}) => {
	const { t } = useTranslation()
	const { bikeProcessState } = useBikeStoreProcess()

	const { currentBike: bike } = bikeProcessState

	const [formFields, setFormFields] = React.useState(
		existingContract
			? {
					iban: existingContract.account_iban,
					owner: existingContract.account_owner,
					phone: existingContract.phone,
					date: existingContract.contract_start_date
						? new Date(existingContract.contract_start_date)
						: new Date(),
					sellerId: existingContract.seller_data?.user_id,
					sellerData: null,
					last_name: existingContract.last_name,
					first_name: existingContract.first_name,
				}
			: {
					...defaultFormFields,
					first_name: bike.bike_owner.first_name ?? "",
					last_name: bike.bike_owner.last_name ?? "",
					phone: bike.bike_owner.phone ?? "",
					owner:
						bike.bike_owner.last_name && bike.bike_owner.first_name
							? bike.bike_owner.last_name + " " + bike.bike_owner.first_name
							: "",
				}
	)

	const [errors, setErrors] = React.useState([])
	const [showLocalErrors, setShowLocalErrors] = React.useState(false)

	const { entity, user } = useAuth()
	const [team, setTeam] = React.useState(null)

	const fetchTeams = React.useCallback(
		() => {
			;(async () => {
				try {
					const { res: getTeam, getError } = await TeamApi.getTeam(entity?.entity_id)
					if (getError()) throw getError()
					const newTeam = getTeam
						.filter((member) => member?.status !== 9)
						.map((member) => {
							return {
								label: member?.description?.nom + " " + member?.description?.prenom,
								value: member?.user_id,
								data: member,
							}
						})
					setTeam(newTeam)
					const selectedUserId = existingContract?.seller_data?.user_id
						? existingContract.seller_data?.user_id
						: user.user_id // Either the seller of the contract or the current user logged in
					const foundSeller = getTeam?.find((b) => b.user_id === selectedUserId) // if no found seller, we take the first one to not leave it empty by default
					setFormFields({
						...formFields,
						sellerId: foundSeller ? selectedUserId : getTeam[0].user_id,
						sellerData: foundSeller ? foundSeller : getTeam[0],
					})
				} catch (error) {
					console.error(error)
				}
			})()
		},
		[] // eslint-disable-line
	)

	React.useEffect(() => {
		// Prefill owner name if we know it (usually right after the bike is sold)
		if (bike?.bike_owner?.first_name && bike?.bike_owner?.last_name) {
			setFormFields({
				...formFields,
				first_name: bike.bike_owner.first_name,
				last_name: bike.bike_owner.last_name,
				owner: bike.bike_owner.last_name + " " + bike.bike_owner.first_name,
			})
		}
		// fetch teams to populate select box
		fetchTeams()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const target = event.target
		const value = target.type === "checkbox" ? target.checked : target.value
		const name = target.name

		setFormFields({
			...formFields,
			[name]: value,
		})
	}

	const handleInputChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
		const target = event.target
		const value = target.value
		const name = target.name

		if (name === "first_name") {
			setFormFields({
				...formFields,
				[name]: value,
				owner: formFields["last_name"] + " " + value,
			})
		} else if (name === "last_name") {
			setFormFields({
				...formFields,
				[name]: value,
				owner: value + " " + formFields["first_name"],
			})
		}
	}

	const handleValidation = React.useCallback((name: string, errs: Array<FormError>) => {
		setErrors((s) => {
			const cleanErrors = [...s.filter((e) => e.origin !== name)]
			return [...cleanErrors, ...errs]
		})
	}, [])

	const handleSubmit = (event: React.SyntheticEvent) => {
		event.preventDefault()
		const formReady = !(errors.length > 0)

		if (!formReady) {
			setShowLocalErrors(true)
		} else {
			onSubmit(formFields)
		}
	}

	const handleCancel = (event: React.SyntheticEvent) => {
		event.preventDefault()
		onCancel(formFields)
	}

	const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleInputChange,
	}

	return (
		<div
			data-cy="form-bike-insurance"
			//className="c-card p-5 md:p-12 mb-8"
			className="mb-8"
		>
			{/* <h2 className="c-h2 font-semibold mb-5 uppercase flex items-center">
				{t("insurance.contractTitle", { ns: "bike" })}
			</h2> */}

			<div className={clsx("flex", { "flex-col": isMobileOnly })}>
				<FormElement
					value={priceYear}
					name="price_year"
					type="number"
					label={t("insurance.price_year", { ns: "bike" })}
					className={clsx("mb-7 w-1/2", {
						"w-full mr-0": isMobileOnly,
						"mr-2": !isMobileOnly,
					})}
					disabled={true}
					{...formElementCommonProps}
				/>

				<FormElement
					value={priceMonth}
					name="price_month"
					type="text"
					label={t("insurance.price_month", { ns: "bike" })}
					className={clsx("mb-7 w-1/2", {
						"w-full ml-0": isMobileOnly,
						"ml-2": !isMobileOnly,
					})}
					disabled={true}
					{...formElementCommonProps}
				/>
			</div>

			<div className={clsx("flex", { "flex-col": isMobileOnly })}>
				<FormElement
					value={formFields.last_name}
					name="last_name"
					type="text"
					label={t("insurance.last_name", { ns: "bike" })}
					className={clsx("mb-7 w-1/2", {
						"w-full mr-0": isMobileOnly,
						"mr-2": !isMobileOnly,
					})}
					customRules={[
						{
							key: "lastname-invalid",
							check: (value) => isNameValidYouSign(value),
							message: t("error_invalid_value", { ns: "form" }),
						},
					]}
					required
					disabled={disabled}
					{...formElementCommonProps}
					onChange={handleInputChangeName}
				/>

				<FormElement
					value={formFields.first_name}
					name="first_name"
					type="text"
					label={t("insurance.first_name", { ns: "bike" })}
					className={clsx("mb-7  w-1/2", {
						"w-full ml-0": isMobileOnly,
						"ml-2": !isMobileOnly,
					})}
					customRules={[
						{
							key: "firstname-invalid",
							check: (value) => isNameValidYouSign(value),
							message: t("error_invalid_value", { ns: "form" }),
						},
					]}
					required
					disabled={disabled}
					{...formElementCommonProps}
					onChange={handleInputChangeName}
				/>
			</div>

			<div className={clsx("flex", { "flex-col": isMobileOnly })}>
				<FormElement
					value={formFields.iban && Ibantools.friendlyFormatIBAN(formFields.iban)}
					name="iban"
					type="text"
					label={t("insurance.iban", { ns: "bike" })}
					className={clsx("mb-7 w-1/2", {
						"w-full mr-0": isMobileOnly,
						"mr-2": !isMobileOnly,
					})}
					customRules={[
						{
							key: "account-number-invalid",
							check: (value: string) => isIBANValid(value),
							message: t("invalid_iban", { ns: "form" }),
						},
					]}
					maxLength={IBAN_MAX_LENGTH}
					disabled={disabled}
					required
					{...formElementCommonProps}
					showErrors={isNotEmpty(formFields.iban) && !isIBANValid(formFields.iban)} // On affiche directement à l'utilisateur, l'erreur si l'iban n'est pas correct
				/>

				<FormElement
					value={formFields.owner}
					name="owner"
					type="text"
					label={t("insurance.owner", { ns: "bike" })}
					className={clsx("mb-7 w-1/2", {
						"w-full ml-0": isMobileOnly,
						"ml-2": !isMobileOnly,
					})}
					customRules={[
						{
							key: "owner-invalid",
							check: (value) => isNameValidYouSign(value),
							message: t("error_invalid_value", { ns: "form" }),
						},
					]}
					required
					disabled={disabled}
					{...formElementCommonProps}
				/>
			</div>
			<div className={clsx("flex", { "flex-col": isMobileOnly })}>
				<FormElement
					value={formFields.phone}
					name="phone"
					type="phone"
					label={t("insurance.phone", { ns: "bike" })}
					className={clsx("mb-7 w-1/2", {
						"w-full mr-0": isMobileOnly,
						"mr-2": !isMobileOnly,
					})}
					required
					disabled={disabled}
					{...formElementCommonProps}
				/>

				<div
					className={clsx(
						"flex mb-7 mr-2 w-1/2 bg-error20 text-error50 rounded p-2 items-center",
						{ "w-full": isMobileOnly }
					)}
				>
					{t("insurance.phone_disclaimer", { ns: "bike" })}
				</div>
			</div>

			<div className={clsx("flex mt-8", { "flex-col": isMobileOnly })}>
				<FormElement
					value={formFields.date}
					name="date"
					type="date"
					min={new Date()}
					label={t("insurance.date_start", { ns: "bike" })}
					className={clsx("mb-7 w-1/2", {
						"w-full mr-0": isMobileOnly,
						"mr-2": !isMobileOnly,
					})}
					required
					disabled={disabled}
					{...formElementCommonProps}
				/>
				<FormElement
					value={formFields.sellerId}
					name="seller"
					defaultValue={team?.find((b) => b.value === formFields.sellerId)}
					options={team}
					type="select"
					label={t("insurance.seller", { ns: "bike" })}
					className={clsx("mb-7 w-1/2", {
						"w-full ml-0": isMobileOnly,
						"mr-2": !isMobileOnly,
					})}
					onChange={(val) =>
						setFormFields({
							...formFields,
							sellerId: val.target.value,
							sellerData: team?.find((b) => b.value === val.target.value).data,
						})
					}
					required
					disabled={disabled}
				/>
			</div>

			{!disabled && (
				<div className={clsx("flex justify-between", { "flex-col": isMobileOnly })}>
					<button
						type="button"
						data-cy="cancel-insurance"
						className="c-btn c-btn--primary uppercase c-btn--m px-7"
						onClick={handleCancel}
					>
						{t("insurance.cancel", { ns: "bike" })}
					</button>

					<button
						data-cy="finish-insurance"
						type="button"
						className={clsx("c-btn c-btn--primary uppercase c-btn--m px-7", {
							"mt-4": isMobileOnly,
						})}
						onClick={handleSubmit}
					>
						{t("insurance.finish", { ns: "bike" })}
					</button>
				</div>
			)}
		</div>
	)
}
